/**
 * ============================================================================
 *  GSAP Animation Engine — Sunset Eyewear (v2)
 *  ---------------------------------------------------------------------------
 *  • Auto-detects animations via class (`gsap-fade-up`) OR data attribute
 *    (`data-gsap="fade-up"`).
 *  • Per-element controls via data attributes:
 *      data-gsap            -> animation type (e.g. "fade-up", "stagger")
 *      data-gsap-duration   -> seconds (default 1)
 *      data-gsap-delay      -> seconds (default 0)
 *      data-gsap-ease       -> any GSAP ease string (default "power2.out")
 *      data-gsap-start      -> ScrollTrigger start (default "top 85%")
 *      data-gsap-stagger    -> seconds between children (default 0.1)
 *      data-gsap-distance   -> px for translate (default 50)
 *      data-gsap-once       -> "true" plays only once (default true)
 *      data-gsap-target     -> selector inside element (otherwise element itself)
 *  • Respects `prefers-reduced-motion`.
 *  • Re-scans DOM on Shopify section reload events (Theme Editor live preview).
 *  • Plays nicely with Lenis smooth scroll (single RAF source).
 * ============================================================================
 */
(function () {
  'use strict';

  const SETTINGS = window.themeSettings || {};
  const ENABLE_GSAP = SETTINGS.enableGsap !== false;
  const ENABLE_LENIS = SETTINGS.enableLenis !== false;
  const LENIS_DURATION = SETTINGS.lenisDuration || 1.4;
  const LENIS_WHEEL    = SETTINGS.lenisWheelMultiplier || 0.9;
  const LENIS_TOUCH    = SETTINGS.lenisTouchMultiplier || 1.5;
  const LENIS_EASING_NAME = SETTINGS.lenisEasing || 'quartic';

  // Easing functions for Lenis
  const LENIS_EASINGS = {
    cubic:   (t) => 1 - Math.pow(1 - t, 3),
    quartic: (t) => 1 - Math.pow(1 - t, 4),
    quintic: (t) => 1 - Math.pow(1 - t, 5),
    expo:    (t) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
  };
  const LENIS_EASING = LENIS_EASINGS[LENIS_EASING_NAME] || LENIS_EASINGS.quartic;
  const AUTO_SECTIONS = SETTINGS.autoAnimateSections === true;
  const AUTO_TYPE     = SETTINGS.autoAnimateType || 'fade-up';
  const AUTO_DURATION = SETTINGS.autoAnimateDuration || 1;
  const AUTO_DISTANCE = SETTINGS.autoAnimateDistance || 50;
  const AUTO_EASE     = SETTINGS.autoAnimateEase || 'power2.out';
  const AUTO_SKIP_FIRST = SETTINGS.autoAnimateSkipFirst === true;

  const IMG_ANIMATION = SETTINGS.imageAnimation || 'none';
  const IMG_DURATION  = SETTINGS.imageDuration || 1.2;
  const IMG_EASE      = SETTINGS.imageEase || 'power2.out';

  const REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------------------
  // Animation Library
  // ---------------------------------------------------------------------------
  // Each entry returns the `from` and `to` state. `d` is the distance value
  // (data-gsap-distance) so user can override per element.
  // ---------------------------------------------------------------------------
  const ANIMATIONS = {
    'fade-up':       (d) => ({ from: { opacity: 0, y:  d }, to: { opacity: 1, y: 0 } }),
    'fade-down':     (d) => ({ from: { opacity: 0, y: -d }, to: { opacity: 1, y: 0 } }),
    'fade-left':     (d) => ({ from: { opacity: 0, x: -d }, to: { opacity: 1, x: 0 } }),
    'fade-right':    (d) => ({ from: { opacity: 0, x:  d }, to: { opacity: 1, x: 0 } }),
    'fade-in':       ()  => ({ from: { opacity: 0 },        to: { opacity: 1 } }),
    'zoom-in':       ()  => ({ from: { opacity: 0, scale: 0.8 }, to: { opacity: 1, scale: 1 } }),
    'zoom-out':      ()  => ({ from: { opacity: 0, scale: 1.2 }, to: { opacity: 1, scale: 1 } }),
    'slide-up':      (d) => ({ from: { y:  d * 2 },        to: { y: 0 } }),
    'slide-down':    (d) => ({ from: { y: -d * 2 },        to: { y: 0 } }),
    'slide-left':    (d) => ({ from: { x: -d * 2 },        to: { x: 0 } }),
    'slide-right':   (d) => ({ from: { x:  d * 2 },        to: { x: 0 } }),
    'rotate-in':     ()  => ({ from: { opacity: 0, rotation: -10 }, to: { opacity: 1, rotation: 0 } }),
    'flip-x':        ()  => ({ from: { opacity: 0, rotationX: 90, transformPerspective: 800 }, to: { opacity: 1, rotationX: 0 } }),
    'flip-y':        ()  => ({ from: { opacity: 0, rotationY: 90, transformPerspective: 800 }, to: { opacity: 1, rotationY: 0 } }),
    'blur-in':       ()  => ({ from: { opacity: 0, filter: 'blur(20px)' }, to: { opacity: 1, filter: 'blur(0px)' } }),
    'mask-reveal-up':   () => ({ from: { clipPath: 'inset(100% 0 0 0)' }, to: { clipPath: 'inset(0% 0 0 0)' } }),
    'mask-reveal-down': () => ({ from: { clipPath: 'inset(0 0 100% 0)' }, to: { clipPath: 'inset(0 0 0% 0)' } }),
    'mask-reveal-left': () => ({ from: { clipPath: 'inset(0 100% 0 0)' }, to: { clipPath: 'inset(0 0% 0 0)' } }),
    'mask-reveal-right':() => ({ from: { clipPath: 'inset(0 0 0 100%)' }, to: { clipPath: 'inset(0 0 0 0%)' } })
  };

  // ---------------------------------------------------------------------------
  // Image-specific animations (parallax, scale, ken burns, etc.)
  // These use ScrollTrigger scrub (tied to scroll position) rather than
  // one-shot playback, giving that cinematic parallax feel.
  // ---------------------------------------------------------------------------
  const IMAGE_ANIMATIONS = {
    'img-parallax-up':    true,
    'img-parallax-down':  true,
    'img-parallax-zoom':  true,
    'img-scale-down':     true,
    'img-scale-up':       true,
    'img-reveal-left':    true,
    'img-reveal-right':   true,
    'img-reveal-up':      true,
    'img-reveal-down':    true,
    'img-ken-burns':      true,
    'img-clip-circle':    true,
    'img-clip-diamond':   true,
    'img-fade':           true
  };

  // Aliases (kept for backward compatibility)
  const ALIASES = {
    fade:        'fade-in',
    up:          'fade-up',
    down:        'fade-down',
    left:        'fade-left',
    right:       'fade-right'
  };

  // ---------------------------------------------------------------------------
  // Text splitter — splits text content into per-word spans wrapped with an
  // overflow-hidden mask, perfect for "word reveal" headline animations.
  // ---------------------------------------------------------------------------
  function splitWords(el) {
    if (el.dataset.gsapSplit === 'true') return el.querySelectorAll('.gsap-word-inner');
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => n.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    });
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const frag = document.createDocumentFragment();
      const words = node.nodeValue.split(/(\s+)/);
      words.forEach((w) => {
        if (!w) return;
        
        const wrap = document.createElement('span');
        wrap.className = 'gsap-word';
        wrap.style.display = 'inline-block';
        wrap.style.overflow = 'hidden';
        wrap.style.verticalAlign = 'top';
        
        if (/^\s+$/.test(w)) {
          wrap.style.whiteSpace = 'pre';
        }
        
        const inner = document.createElement('span');
        inner.className = 'gsap-word-inner';
        inner.style.display = 'inline-block';
        inner.style.willChange = 'transform';
        inner.textContent = w;
        
        wrap.appendChild(inner);
        frag.appendChild(wrap);
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.dataset.gsapSplit = 'true';
    return el.querySelectorAll('.gsap-word-inner');
  }

  const SUPPORTED_TYPES = Object.keys(ANIMATIONS).concat(Object.keys(ALIASES));

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------
  function attr(el, name, fallback) {
    const v = el.getAttribute(name);
    return v === null || v === '' ? fallback : v;
  }
  function num(el, name, fallback) {
    const v = parseFloat(el.getAttribute(name));
    return isNaN(v) ? fallback : v;
  }

  /**
   * Resolve animation type from element. Looks for:
   *   1) data-gsap="type"
   *   2) class "gsap-{type}"
   */
  function resolveType(el) {
    let type = el.getAttribute('data-gsap');
    if (!type) {
      const cls = Array.from(el.classList).find((c) => c.startsWith('gsap-'));
      if (cls) type = cls.replace(/^gsap-/, '');
    }
    if (!type) return null;
    return ALIASES[type] || type;
  }

  function show(el) {
    el.style.visibility = 'visible';
    el.style.opacity = '';
  }

  // ---------------------------------------------------------------------------
  // Reduced motion / GSAP disabled fallback
  // ---------------------------------------------------------------------------
  function revealAll() {
    document
      .querySelectorAll('[data-gsap], [class*="gsap-"]')
      .forEach((el) => {
        if (
          el.classList.contains('gsap-ready') ||
          el.hasAttribute('data-gsap') ||
          Array.from(el.classList).some((c) => c.startsWith('gsap-'))
        ) {
          show(el);
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Animator
  // ---------------------------------------------------------------------------
  function animateElement(el) {
    if (el.dataset.gsapApplied === 'true') return;
    el.dataset.gsapApplied = 'true';

    const type = resolveType(el);
    if (!type || (!ANIMATIONS[type] && type !== 'stagger' && type !== 'text-reveal' && type !== 'text-fade' && !IMAGE_ANIMATIONS[type])) {
      show(el);
      return;
    }

    const distance = num(el, 'data-gsap-distance', 50);
    const duration = num(el, 'data-gsap-duration', 1);
    const delay    = num(el, 'data-gsap-delay', 0);
    const ease     = attr(el, 'data-gsap-ease', 'power2.out');
    const start    = attr(el, 'data-gsap-start', 'top 85%');
    const once     = attr(el, 'data-gsap-once', 'true') !== 'false';
    const stagger  = num(el, 'data-gsap-stagger', 0.1);

    // -------- Image-specific animations (parallax/scrub) --------
    if (IMAGE_ANIMATIONS[type]) {
      gsap.set(el, { visibility: 'visible', overflow: 'hidden' });
      const img = el.querySelector('img, video, picture') || el;
      const scrubVal = num(el, 'data-gsap-scrub', 1.5);
      // GPU-accelerate image
      gsap.set(img, { willChange: 'transform' });

      switch (type) {
        case 'img-parallax-up':
          gsap.set(img, { scale: 1.15 });
          gsap.to(img, {
            yPercent: -15,
            ease: 'none',
            force3D: true,
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: scrubVal }
          });
          break;

        case 'img-parallax-down':
          gsap.set(img, { scale: 1.15 });
          gsap.to(img, {
            yPercent: 15,
            ease: 'none',
            force3D: true,
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: scrubVal }
          });
          break;

        case 'img-parallax-zoom':
          gsap.fromTo(img,
            { scale: 1.3 },
            { scale: 1, ease: 'none', force3D: true, scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: scrubVal } }
          );
          break;

        case 'img-scale-down':
          gsap.fromTo(img,
            { scale: 1.2 },
            { scale: 1, ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' }, onComplete: () => gsap.set(img, { willChange: 'auto' }) }
          );
          break;

        case 'img-scale-up':
          gsap.fromTo(img,
            { scale: 0.85, opacity: 0.6 },
            { scale: 1, opacity: 1, ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' }, onComplete: () => gsap.set(img, { willChange: 'auto' }) }
          );
          break;

        case 'img-reveal-left':
          gsap.fromTo(el,
            { clipPath: 'inset(0 100% 0 0)' },
            { clipPath: 'inset(0 0% 0 0)', ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' } }
          );
          gsap.set(img, { scale: 1.1 });
          gsap.to(img, { scale: 1, duration: duration * 1.5, delay: delay, ease: ease, force3D: true, onComplete: () => gsap.set(img, { willChange: 'auto' }) });
          break;

        case 'img-reveal-right':
          gsap.fromTo(el,
            { clipPath: 'inset(0 0 0 100%)' },
            { clipPath: 'inset(0 0 0 0%)', ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' } }
          );
          gsap.set(img, { scale: 1.1 });
          gsap.to(img, { scale: 1, duration: duration * 1.5, delay: delay, ease: ease, force3D: true, onComplete: () => gsap.set(img, { willChange: 'auto' }) });
          break;

        case 'img-reveal-up':
          gsap.fromTo(el,
            { clipPath: 'inset(100% 0 0 0)' },
            { clipPath: 'inset(0% 0 0 0)', ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' } }
          );
          gsap.set(img, { scale: 1.1 });
          gsap.to(img, { scale: 1, duration: duration * 1.5, delay: delay, ease: ease, force3D: true, onComplete: () => gsap.set(img, { willChange: 'auto' }) });
          break;

        case 'img-reveal-down':
          gsap.fromTo(el,
            { clipPath: 'inset(0 0 100% 0)' },
            { clipPath: 'inset(0 0 0% 0)', ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' } }
          );
          gsap.set(img, { scale: 1.1 });
          gsap.to(img, { scale: 1, duration: duration * 1.5, delay: delay, ease: ease, force3D: true, onComplete: () => gsap.set(img, { willChange: 'auto' }) });
          break;

        case 'img-ken-burns':
          gsap.fromTo(img,
            { scale: 1 },
            { scale: 1.15, ease: 'none', force3D: true, scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: scrubVal } }
          );
          break;

        case 'img-clip-circle':
          gsap.fromTo(el,
            { clipPath: 'circle(0% at 50% 50%)' },
            { clipPath: 'circle(75% at 50% 50%)', ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' }, onComplete: () => gsap.set(img, { willChange: 'auto' }) }
          );
          break;

        case 'img-clip-diamond':
          gsap.fromTo(el,
            { clipPath: 'polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%)' },
            { clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)', ease: ease, duration: duration, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' }, onComplete: () => gsap.set(img, { willChange: 'auto' }) }
          );
          break;

        case 'img-fade':
          gsap.fromTo(img,
            { opacity: 0, y: 20, scale: 1.02 },
            { opacity: 1, y: 0, scale: 1, ease: ease, duration: duration * 1.2, delay: delay, force3D: true, scrollTrigger: { trigger: el, start: start, toggleActions: once ? 'play none none none' : 'play none none reverse' }, onComplete: () => gsap.set(img, { willChange: 'auto' }) }
          );
          break;
      }
      return;
    }

    // -------- Text reveal modes --------
    // text-reveal : word mask reveal from bottom (cinematic headline)
    // text-fade   : word fade-in stagger (gentler)
    if (type === 'text-reveal' || type === 'text-fade') {
      const words = splitWords(el);
      gsap.set(el, { visibility: 'visible' });

      if (type === 'text-reveal') {
        gsap.set(words, { yPercent: 110 });
        gsap.to(words, {
          yPercent: 0,
          duration: duration,
          delay: delay,
          ease: ease,
          stagger: stagger,
          scrollTrigger: {
            trigger: el,
            start: start,
            toggleActions: once ? 'play none none none' : 'play none none reverse'
          }
        });
      } else {
        gsap.set(words, { opacity: 0, y: distance * 0.5 });
        gsap.to(words, {
          opacity: 1,
          y: 0,
          duration: duration,
          delay: delay,
          ease: ease,
          stagger: stagger,
          scrollTrigger: {
            trigger: el,
            start: start,
            toggleActions: once ? 'play none none none' : 'play none none reverse'
          }
        });
      }
      return;
    }

    // Stagger mode: animates direct children with the parent's data-gsap
    // type prefix (`stagger-fade-up`) or default to fade-up.
    if (type === 'stagger' || type.startsWith('stagger-')) {
      const childType = type === 'stagger'
        ? (el.getAttribute('data-gsap-children') || 'fade-up')
        : type.replace(/^stagger-/, '');

      const builder = ANIMATIONS[ALIASES[childType] || childType];
      if (!builder) { show(el); return; }
      const { from, to } = builder(distance);

      const targetSelector = el.getAttribute('data-gsap-target');
      const children = targetSelector
        ? el.querySelectorAll(targetSelector)
        : el.children;

      gsap.set(el, { visibility: 'visible' });
      gsap.set(children, { ...from, willChange: 'transform, opacity' });
      gsap.to(children, {
        ...to,
        duration: duration,
        delay: delay,
        ease: ease,
        stagger: stagger,
        force3D: true,
        scrollTrigger: {
          trigger: el,
          start: start,
          toggleActions: once ? 'play none none none' : 'play none none reverse'
        },
        onComplete: function () {
          gsap.set(children, { willChange: 'auto' });
        }
      });
      return;
    }

    // Standard single-element animation
    const builder = ANIMATIONS[type];
    if (!builder) { show(el); return; }
    const { from, to } = builder(distance);

    const targetSelector = el.getAttribute('data-gsap-target');
    const target = targetSelector ? el.querySelector(targetSelector) : el;

    // GPU-accelerate: hint browser before animation
    gsap.set(target, { willChange: 'transform, opacity' });
    gsap.set(el, { visibility: 'visible' });
    gsap.fromTo(target, from, {
      ...to,
      duration: duration,
      delay: delay,
      ease: ease,
      force3D: true,
      scrollTrigger: {
        trigger: el,
        start: start,
        toggleActions: once ? 'play none none none' : 'play none none reverse'
      },
      onComplete: function () {
        // Release GPU memory after animation finishes
        gsap.set(target, { willChange: 'auto' });
      }
    });
  }

  function scan(root) {
    (root || document)
      .querySelectorAll('[data-gsap]:not([data-gsap-applied="true"]), [class*="gsap-"]:not([data-gsap-applied="true"])')
      .forEach((el) => {
        // Make sure it's actually a gsap-{type} class, not e.g. gsap-ready
        const hasDataAttr = el.hasAttribute('data-gsap');
        const hasGsapClass = Array.from(el.classList).some(
          (c) => c.startsWith('gsap-') &&
                 SUPPORTED_TYPES.some((t) => c === 'gsap-' + t || c === 'gsap-stagger' || c.startsWith('gsap-stagger-'))
        );
        if (hasDataAttr || hasGsapClass) animateElement(el);
      });
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  function bootLenis() {
    if (!ENABLE_LENIS || typeof Lenis === 'undefined' || REDUCED_MOTION) return null;
    const lenis = new Lenis({
      duration: LENIS_DURATION,
      easing: LENIS_EASING,
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: LENIS_WHEEL,
      touchMultiplier: LENIS_TOUCH,
      infinite: false,
      autoResize: true
    });
    document.documentElement.classList.add('lenis', 'lenis-smooth');

    // Handle anchor links smoothly
    document.querySelectorAll('a[href^="#"]').forEach((a) => {
      a.addEventListener('click', () => {
        lenis.scrollTo(a.getAttribute('href'), { offset: 0 });
      });
    });

    return lenis;
  }

  function init() {
    // ---- Reduced motion or GSAP disabled ----
    if (REDUCED_MOTION || !ENABLE_GSAP || typeof gsap === 'undefined') {
      revealAll();
      bootLenisStandalone();
      return;
    }

    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
      // Reduce ScrollTrigger callback frequency for smoother performance
      ScrollTrigger.config({ limitCallbacks: true });
    }

    // Force GSAP to use GPU-accelerated transforms
    gsap.config({ force3D: true });

    // Boot Lenis IMMEDIATELY — smooth scroll starts before DOM is fully parsed
    const lenis = bootLenis();
    // Expose globally so modals/drawers can stop/start it
    if (lenis) window.SunsetLenis = lenis;

    if (lenis && typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
    } else if (lenis) {
      function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
    }

    // Defer animation scan to after DOM is ready (Lenis already running)
    function runScan() {
      scan(document);
      if (AUTO_SECTIONS) autoAnimateSections();
      autoAnimateImages(document);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runScan);
    } else {
      runScan();
    }

    // Re-scan on Shopify Theme Editor events
    document.addEventListener('shopify:section:load', (e) => {
      if (AUTO_SECTIONS) tagSection(e.target);
      autoAnimateImages(e.target);
      scan(e.target);
    });
    document.addEventListener('shopify:section:reorder', () => scan(document));
    document.addEventListener('shopify:block:select', (e) => scan(e.target));
  }

  // Standalone Lenis (GSAP off, smooth scroll on)
  function bootLenisStandalone() {
    if (!ENABLE_LENIS || typeof Lenis === 'undefined' || REDUCED_MOTION) return;
    const lenis = new Lenis({
      duration: LENIS_DURATION,
      easing: LENIS_EASING,
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: LENIS_WHEEL,
      touchMultiplier: LENIS_TOUCH,
      infinite: false,
      autoResize: true
    });
    document.documentElement.classList.add('lenis', 'lenis-smooth');
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
  }

  function tagSection(section) {
    if (!section || !section.classList) return;
    if (section.classList.contains('gsap-skip')) return;
    if (section.dataset.gsapApplied === 'true') return;
    if (section.hasAttribute('data-gsap')) return;
    if (Array.from(section.classList).some((c) => c.startsWith('gsap-'))) return;
    if (section.querySelector('[data-gsap], [class*="gsap-fade-"], [class*="gsap-zoom-"], [class*="gsap-slide-"], [class*="gsap-mask-"], [class*="gsap-blur-"], [class*="gsap-rotate-"], [class*="gsap-flip-"], [class*="gsap-text-"], [class*="gsap-stagger"], .gsap-skip')) {
      return;
    }
    section.setAttribute('data-gsap', AUTO_TYPE);
    section.setAttribute('data-gsap-duration', AUTO_DURATION);
    section.setAttribute('data-gsap-distance', AUTO_DISTANCE);
    section.setAttribute('data-gsap-ease', AUTO_EASE);
    animateElement(section);
  }

  function autoAnimateSections() {
    const sections = document.querySelectorAll('main .shopify-section');
    sections.forEach((s, i) => {
      if (AUTO_SKIP_FIRST && i === 0) return;
      tagSection(s);
    });
  }

  function autoAnimateImages(root) {
    if (IMG_ANIMATION === 'none') return;
    const selectors = [
      'figure:not([data-gsap-applied])',
      '.content-box__background-media:not([data-gsap-applied])',
      '.product-card__media:not([data-gsap-applied])',
      '.section-split__column--image:not([data-gsap-applied])',
      '.media-blocks__item:not([data-gsap-applied])',
      '.hot-spots__image:not([data-gsap-applied])',
      '.links-with-image__item:not([data-gsap-applied])'
    ].join(', ');
    const els = (root || document).querySelectorAll(selectors);
    els.forEach((el) => {
      if (el.hasAttribute('data-gsap')) return;
      if (el.dataset.gsapApplied === 'true') return;
      if (Array.from(el.classList).some((c) => c.startsWith('gsap-'))) return;
      if (el.closest('[data-gsap]')) return;
      el.setAttribute('data-gsap', IMG_ANIMATION);
      el.setAttribute('data-gsap-duration', IMG_DURATION);
      el.setAttribute('data-gsap-ease', IMG_EASE);
      animateElement(el);
    });
  }

  // Run immediately — Lenis starts before DOM is fully parsed for instant smoothness
  init();
})();
